/**
 * Pure logic behind the three public auth pages (TASK-449).
 *
 * Extracted from the components because the admin's vitest runs in a `node`
 * environment (see `vitest.config.ts`) — validation rules and error mapping
 * are unit-tested here, the components stay thin.
 *
 * Password policy (min 12 / max 255) is NOT invented here: it mirrors the two
 * schemas the backend validates against —
 * `acceptInviteRequestSchema` (packages/shared/src/schemas/admin-invite.ts)
 * and `passwordResetConfirmSchema` (packages/shared/src/schemas/auth.ts).
 * `auth-forms.test.ts` pins both so the UI can never accept a password the
 * API rejects.
 */

import { ApiError } from '@/lib/api-client';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 255;

/**
 * Every invite / reset token the backend mints is `randomBytes(32)` rendered
 * as lowercase hex — 64 chars (auth.service.acceptInvite, PasswordResetService,
 * SelectorsService). Checking the shape client-side turns a mangled link into
 * an explanatory screen instead of a pointless round-trip.
 */
const TOKEN_PATTERN = /^[0-9a-f]{64}$/;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidTokenFormat(token: string | null | undefined): boolean {
  return typeof token === 'string' && TOKEN_PATTERN.test(token);
}

export function validatePassword(value: string): string | null {
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Минимум ${PASSWORD_MIN_LENGTH} символов`;
  }
  if (value.length > PASSWORD_MAX_LENGTH) {
    return `Не длиннее ${PASSWORD_MAX_LENGTH} символов`;
  }
  return null;
}

export function validatePasswordConfirm(
  value: string,
  values: { password: string },
): string | null {
  return value === values.password ? null : 'Пароли не совпадают';
}

export function validateEmail(value: string): string | null {
  return EMAIL_PATTERN.test(value.trim()) ? null : 'Введите корректный email';
}

export interface AuthAlert {
  color: 'red' | 'yellow';
  title?: string;
  message: string;
}

export const INVALID_LINK_ALERT: AuthAlert = {
  color: 'red',
  title: 'Ссылка недействительна',
  message:
    'Ссылка устарела, уже использована или скопирована не полностью. Запросите новую.',
};

const SERVER_ALERT: AuthAlert = {
  color: 'red',
  title: 'Сервер недоступен',
  message: 'Не удалось связаться с сервером. Попробуйте позже.',
};

const NETWORK_ALERT: AuthAlert = {
  color: 'red',
  message: 'Нет связи с сервером. Проверьте подключение и попробуйте ещё раз.',
};

const THROTTLED_ALERT: AuthAlert = {
  color: 'yellow',
  title: 'Слишком много попыток',
  message: 'Превышен лимит запросов. Попробуйте позже.',
};

/**
 * Error mapping for both token-redeeming forms (accept-invite, reset-confirm).
 * The wire cases are identical: 410 GONE for an unknown / expired / consumed
 * token, 400 VALIDATION_ERROR for a payload the schema rejects, 429 for the
 * per-IP throttle. Raw backend messages are never rendered.
 */
export function describeTokenSubmitError(err: unknown): AuthAlert {
  if (!(err instanceof ApiError)) return NETWORK_ALERT;
  if (err.status === 410) return INVALID_LINK_ALERT;
  if (err.status === 400) {
    return {
      color: 'red',
      message: `Пароль не соответствует требованиям: минимум ${PASSWORD_MIN_LENGTH} символов.`,
    };
  }
  if (err.status === 429) return THROTTLED_ALERT;
  if (err.status >= 500) return SERVER_ALERT;
  return {
    color: 'red',
    message: 'Не удалось сохранить пароль. Попробуйте ещё раз.',
  };
}

/**
 * Error mapping for the reset-REQUEST form.
 *
 * Anti-enumeration: `POST /auth/password-reset/request` answers 204 whether or
 * not the address exists, so there is deliberately NO branch that could tell a
 * probing visitor "no such user" — every non-throttle failure collapses into
 * one generic message.
 */
export function describeResetRequestError(err: unknown): AuthAlert {
  if (err instanceof ApiError && err.status === 429) return THROTTLED_ALERT;
  return {
    color: 'red',
    message: 'Не удалось отправить письмо. Попробуйте позже.',
  };
}

/**
 * Shown after a successful request submit — identical for every address, which
 * is the whole point (admin-arch §3.2 anti-enumeration).
 */
export const RESET_REQUEST_NEUTRAL_NOTICE =
  'Если такой адрес есть в системе, мы отправили на него ссылку для сброса пароля. Ссылка действует 2 часа.';

/**
 * `POST /auth/accept-invite` (admin + account-team invites) and
 * `POST /public/selectors/accept-invite` (selector invites re-issued from the
 * selector card) mint indistinguishable 64-hex tokens and — with the env
 * values documented in the impl report — land on the SAME SPA page. A 410 from
 * the first endpoint therefore means "not an admin invite", so the selector
 * endpoint is tried before giving up. Any other failure is terminal.
 */
export function shouldRetryAsSelectorInvite(err: unknown): boolean {
  return err instanceof ApiError && err.status === 410;
}

/**
 * Success notices handed to `/login` via router state after a redirect.
 */
export const LOGIN_NOTICE = {
  inviteAccepted: 'invite-accepted',
  passwordReset: 'password-reset',
} as const;

export type LoginNotice = (typeof LOGIN_NOTICE)[keyof typeof LOGIN_NOTICE];

const LOGIN_NOTICE_TEXT: Record<LoginNotice, string> = {
  [LOGIN_NOTICE.inviteAccepted]: 'Пароль сохранён. Войдите с новым паролем.',
  [LOGIN_NOTICE.passwordReset]: 'Пароль изменён. Войдите с новым паролем.',
};

export function loginNoticeFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const notice = (state as { notice?: unknown }).notice;
  if (typeof notice !== 'string') return null;
  return LOGIN_NOTICE_TEXT[notice as LoginNotice] ?? null;
}
